/**
 * 管理画面共通レイアウト (Phase 3 — Figma 準拠).
 *
 * /sites/$siteId 以下の全画面で使われる。
 * - 232px サイドバー: ロゴ、サイト切替ドロップダウン、ナビ
 * - 52px トップバー: ページタイトル、ユーザーアバター
 * - コンテンツ: 子ルート (Outlet)
 *
 * 色は DESIGN.md 準拠（Figma レイアウトのみ参照）。
 */

import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { authLogout, authMe, fetchMySites, fetchSite } from '../lib/api-client.js';

interface NavItem {
  path: string;
  label: string;
  /** prefix match で active 判定する prefix */
  activePrefix?: string;
}

/** Figma nav 順序に準拠 */
const NAV_ITEMS: NavItem[] = [
  { path: '/dashboard', label: 'ダッシュボード' },
  { path: '/posts', label: '記事管理', activePrefix: '/posts' },
  { path: '/snippets', label: 'スニペット' },
  { path: '/categories', label: 'カテゴリ' },
  { path: '/media', label: 'メディア' },
  { path: '/embed', label: '埋め込みコード' },
  { path: '/activity-log', label: 'アクティビティ' },
  { path: '/settings/general', label: 'サイト設定', activePrefix: '/settings' },
];

export function AppLayout() {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const params = useParams({ strict: false }) as { siteId?: string };
  const siteId = params.siteId ?? '';
  const currentPath = routerState.location.pathname;

  const [showSiteMenu, setShowSiteMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = async () => {
    await authLogout();
    navigate({ to: '/login' });
  };

  // 現在のサイト情報
  const { data: currentSite } = useQuery({
    queryKey: ['sites', siteId],
    queryFn: () => fetchSite(siteId),
    enabled: !!siteId,
    staleTime: 60_000,
  });

  // ユーザー情報（アバター用）
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authMe,
    staleTime: 300_000,
  });

  // サイト一覧（切替ドロップダウン用）
  const { data: allSites } = useQuery({
    queryKey: ['me', 'sites'],
    queryFn: fetchMySites,
    staleTime: 60_000,
    enabled: showSiteMenu,
  });

  const avatarInitial = me?.email?.[0]?.toUpperCase() ?? 'U';

  const activeNav = NAV_ITEMS.find((i) => {
    const fullPath = `/sites/${siteId}${i.path}`;
    const activePath = i.activePrefix ? `/sites/${siteId}${i.activePrefix}` : fullPath;
    return currentPath === fullPath || currentPath.startsWith(activePath);
  });

  // Set document title based on active navigation
  const pageTitle = activeNav?.label ?? 'PetaCMS';
  useEffect(() => {
    document.title = `${pageTitle} - PetaCMS`;
  }, [pageTitle]);

  const handleSiteSwitch = (newSiteId: string) => {
    setShowSiteMenu(false);
    try {
      localStorage.setItem('petacms_last_site_id', newSiteId);
    } catch {
      // ignore
    }
    navigate({ to: '/sites/$siteId/dashboard', params: { siteId: newSiteId } });
  };

  return (
    <>
      <div className="flex min-h-screen bg-[#f8f7f6] text-[#23221e]">
        {/* ─── Sidebar (232px, Figma 準拠) ─── */}
        <aside className="flex w-[232px] shrink-0 flex-col border-r border-[#edebe8] bg-[#f2f1f0]">
          {/* Logo */}
          <div className="px-5 pb-4 pt-[18px]">
            <Link to="/sites" className="text-[19px] font-bold text-[#23221e]">
              PetaCMS
            </Link>
          </div>

          {/* Site selector dropdown */}
          <div className="relative px-5 pb-4">
            <button
              type="button"
              onClick={() => setShowSiteMenu(!showSiteMenu)}
              className="flex w-full items-center justify-between rounded-[6px] border border-[#d6d3d0] bg-white px-3 py-[9px] text-left text-[13px] font-medium text-[#23221e] transition hover:border-[#3b82f6]"
            >
              <span className="truncate">{currentSite?.name ?? '読み込み中...'}</span>
              <span className="ml-1 text-[#706d65]">&#9662;</span>
            </button>

            {/* Site dropdown menu */}
            {showSiteMenu && (
              <>
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: overlay backdrop */}
                {/* biome-ignore lint/a11y/noStaticElementInteractions: overlay backdrop */}
                <div className="fixed inset-0 z-10" onClick={() => setShowSiteMenu(false)} />
                <div className="absolute left-5 right-5 top-full z-20 mt-1 rounded-[6px] border border-[#edebe8] bg-white py-1 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
                  {allSites?.map((site) => (
                    <button
                      key={site.id}
                      type="button"
                      onClick={() => handleSiteSwitch(site.id)}
                      className={`flex w-full items-center px-3 py-2 text-left text-[13px] transition hover:bg-[#f8f7f6] ${
                        site.id === siteId ? 'font-semibold text-[#3b82f6]' : 'text-[#23221e]'
                      }`}
                    >
                      <span className="truncate">{site.name}</span>
                    </button>
                  ))}
                  <div className="border-t border-[#edebe8] mt-1 pt-1">
                    <Link
                      to="/sites"
                      onClick={() => setShowSiteMenu(false)}
                      className="flex w-full items-center px-3 py-2 text-left text-[13px] text-[#706d65] transition hover:bg-[#f8f7f6]"
                    >
                      すべてのサイト →
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1">
            {NAV_ITEMS.map((item) => {
              const fullPath = `/sites/${siteId}${item.path}`;
              const activePath = item.activePrefix
                ? `/sites/${siteId}${item.activePrefix}`
                : fullPath;
              const active = currentPath === fullPath || currentPath.startsWith(activePath);
              return (
                <Link
                  key={item.path}
                  to={`/sites/$siteId${item.path}` as '/sites/$siteId/dashboard'}
                  params={{ siteId }}
                  className={`flex h-10 items-center text-[14px] transition ${
                    active
                      ? 'border-l-[3px] border-[#3b82f6] bg-[#eff6ff] pl-[21px] font-semibold text-[#3b82f6]'
                      : 'border-l-[3px] border-transparent pl-[21px] text-[#706d65] hover:bg-[#edebe8]'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* ─── Main area ─── */}
        <div className="flex flex-1 flex-col">
          {/* Top bar (52px, Figma 準拠) */}
          <header className="flex h-[52px] shrink-0 items-center justify-between border-b border-[#edebe8] bg-white px-7">
            <h1 className="text-[16px] font-semibold text-[#23221e]">
              {activeNav?.label ?? 'PetaCMS'}
            </h1>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex size-8 items-center justify-center rounded-full bg-[#3b82f6] text-[14px] font-semibold text-white transition hover:bg-[#1d4ed8]"
              >
                {avatarInitial}
              </button>
              {showUserMenu && (
                <>
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: overlay backdrop */}
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: overlay backdrop */}
                  <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute right-0 top-full z-20 mt-2 w-48 rounded-[6px] border border-[#edebe8] bg-white py-1 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
                    {me?.email && (
                      <div className="border-b border-[#edebe8] px-3 py-2 text-[12px] text-[#706d65]">
                        {me.email}
                      </div>
                    )}
                    <Link
                      to="/account"
                      onClick={() => setShowUserMenu(false)}
                      className="flex w-full items-center px-3 py-2 text-left text-[13px] text-[#23221e] transition hover:bg-[#f8f7f6]"
                    >
                      アカウント設定
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center px-3 py-2 text-left text-[13px] text-[#ef4444] transition hover:bg-[#fef2f2]"
                    >
                      ログアウト
                    </button>
                  </div>
                </>
              )}
            </div>
          </header>

          {/* Content area */}
          <main className="flex-1 p-7">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
